import { z } from "zod";
import { ReasonCode } from "./permissions.ts";

/**
 * Error codes of the API (07 §1 K-04). Fixed identifiers, one per HTTP situation:
 * - `unauthenticated` 401, no or expired session
 * - `forbidden` 403, a permitted view with a forbidden action; carries `reason`
 * - `not_found` 404, also for data of another customer (K-05, never 403)
 * - `validation_failed` 400, the body or query did not match its schema; `details` carries the issues
 * - `payload_too_large` 413, the body is over the API's limit of 64 KB; refused before it is read
 *   as JSON (N6 of the review 2026-10-19)
 * - `conflict` 409, the state does not allow the action (open order, living entitlement); carries `reason`
 * - `limit_exceeded` 409, the credit limit would be exceeded (R-04); carries `reason`
 * - `rate_limited` 429
 * - `instance_mismatch` 403, the login over an instance the platform does not allow
 *   (`CUSTOMER_INSTANCE_HOSTS`, ADR-126); same name as the callback's `LoginErrorCode`, which the
 *   portal answers with `/login?error=instance_mismatch` (08 §1)
 * - `upstream` 502, dataCycle or OpenRouter did not answer; 503 where the login cannot reach the
 *   central instance or its client is not configured (`POST /api/auth/login/start`, 07 §2.3)
 * - `internal` 500, also for a configuration missing on the server (`key/reveal`, H-10)
 */
export const ErrorCode = z.enum([
  "unauthenticated",
  "forbidden",
  "not_found",
  "validation_failed",
  "payload_too_large",
  "conflict",
  "limit_exceeded",
  "rate_limited",
  "instance_mismatch",
  "upstream",
  "internal",
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

/**
 * The single error shape of the API: `{ error: { code, message, reason?, details? } }` (K-04).
 * `reason` is the code of the permission function, so the portal shows the same text the
 * execution used. `details` is free for validation issues and never contains personal data.
 */
export const ApiError = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    reason: ReasonCode.optional(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiError>;
